// THE CARD PATH'S EMAIL, ITS ERRORS, AND THE ATTEMPT A RETRY REUSES.
// Kept free of imports so it runs under `node --test` (payer-email.test.mjs).
//
// 12 Oct 2026, a lost booking (iOS Safari, three tries): our check
// (/^[^\s@]+@[^\s@]+\.[^\s@]+$/) passed an address Stripe then refused at
// confirmSetup ("email_invalid", "Your email address is invalid."), shown as a
// banner above the form rather than on the field. Each try had already made a
// guest account and a draft order (5 of each for one person), and a reload
// later lost the typed email. docs/EMBEDDED_CHECKOUT.md "Retries and errors".

// Card path only (a wallet hands over its own email). Robin, 12 Oct 2026:
// "ok so lets validate the mail if pay by card, not too rigorously, maybe
// white space we trim ourselves rather than force them to change?" So the
// address is cleaned silently first and shown back in the field: all
// whitespace out, lower case, no dots or comma hanging off the ends of
// either part, and in the domain a comma becomes a dot and ".." one dot.
//
// cleanEmailWithFixes also says which fixes it made, for PostHog
// (email_cleaned `fixes`, never the address): whitespace, lowercase,
// trailing_comma, edge_dot, comma_to_dot, double_dot.
export const cleanEmailWithFixes = raw => {
  const fixes = []
  const step = (value, fix, next) => {
    const out = next(value)
    if (out !== value && !fixes.includes(fix)) fixes.push(fix)
    return out
  }
  let email = String(raw || '')
  email = step(email, 'whitespace', v => v.replace(/\s+/g, ''))
  email = step(email, 'lowercase', v => v.toLowerCase())
  email = step(email, 'trailing_comma', v => v.replace(/,+$/, ''))
  const edgeDots = v => v.replace(/^\.+|\.+$/g, '')
  email = step(email, 'edge_dot', edgeDots)
  const at = email.lastIndexOf('@')
  if (at < 0) return { email, fixes }
  const local = step(email.slice(0, at), 'edge_dot', edgeDots)
  let domain = step(email.slice(at + 1), 'comma_to_dot', v => v.replace(/,/g, '.'))
  domain = step(domain, 'double_dot', v => v.replace(/\.{2,}/g, '.'))
  domain = step(domain, 'edge_dot', edgeDots)
  return { email: `${local}@${domain}`, fixes }
}

export const cleanEmail = raw => cleanEmailWithFixes(raw).email

// Then a light check only: one @, something before it, and a dot in the
// domain with two or more letters after the last one. null when fine, else
// the rule it failed, for email_unusable `rule`: missing, no_at, multiple_at,
// no_local, no_domain_dot, short_tld. Anything subtler is Stripe's to
// refuse, and its refusal lands on the field (isEmailError).
export const emailRuleFailed = email => {
  if (!email) return 'missing'
  const parts = email.split('@')
  if (parts.length < 2) return 'no_at'
  if (parts.length > 2) return 'multiple_at'
  if (!parts[0]) return 'no_local'
  const labels = parts[1].split('.')
  if (labels.length < 2 || labels.some(l => !l)) return 'no_domain_dot'
  return /^[a-z]{2,}$/i.test(labels[labels.length - 1]) ? null : 'short_tld'
}

// email_unusable `reason`: 'missing' or 'not_an_email', or null when fine.
export const emailProblem = email => {
  const rule = emailRuleFailed(email)
  return rule ? (rule === 'missing' ? 'missing' : 'not_an_email') : null
}

// The card path's one decision: the address it will use, and `problem` when
// even the cleaned one is no address.
//
// `problem` DECIDES ONLY WHAT WE STORE (1 Nov 2026). Until today it stopped
// the press - "yea if we can't tidy it obviously then we need to ask them to
// fix it" (Robin, 12 Oct) - and that was the last of the four refusals on the
// pay path: "we dont validate for fields at pay, if customer is willing to
// pay we let them and aider resolves missing info.." (31 Oct). So the booking
// now goes with no address at all rather than with one we cannot send to,
// exactly as an unusable mobile is dropped by Api::V2::UsersController
// #guest_signup; Order#missing_details then reports :email and Aider asks.
// The cleaning stays: it fixes real typos without asking anybody anything.
export const cardPayerEmail = ({ typed, known }) => {
  if (known) return { email: String(known).trim().toLowerCase(), problem: null, rule: null, fixes: [] }
  const { email, fixes } = cleanEmailWithFixes(typed)
  const rule = emailRuleFailed(email)
  return { email, problem: emailProblem(email), rule, fixes }
}

// The one email message left, and it is Stripe's refusal at confirm, put on
// the field it belongs to (isEmailError below). "Add your email for the
// booking confirmation" is gone with the refusal that said it.
export const EMAIL_INVALID = 'Check your email address'

// A Stripe error about the email (its code, or the parameter it names:
// `param`, docs.stripe.com/api/errors), which belongs on the email field.
export const isEmailError = error =>
  !!error && (error.code === 'email_invalid' || /email/i.test(String(error.param || '')))

// The bank said no, or the 3DS check failed or was cancelled.
const BANK_CODES = ['setup_intent_authentication_failure', 'authentication_failure', 'card_declined',
  'payment_method_provider_decline', 'setup_attempt_failed', 'payment_intent_authentication_failure']
export const BANK_DIDNT_CONFIRM = "Your bank didn't confirm this card. Try again, use another card, or pay with Apple Pay."

// What a failed card confirm says under the Book button. The bank's refusals
// get one plain sentence; any other card error (a wrong CVC, an expired card)
// Stripe's own, which names the field.
export const cardErrorMessage = error => {
  if (!error) return BANK_DIDNT_CONFIRM
  if (BANK_CODES.includes(error.code) || error.decline_code) return BANK_DIDNT_CONFIRM
  if (error.type === 'card_error' && error.message) return error.message
  return BANK_DIDNT_CONFIRM
}

// The attempt this tab is on: a key the server reuses the draft order and
// SetupIntent by (api/v2/orders/embedded_checkout `checkout_key`), the guest
// this tab already signed up, and the email typed beside the card. In
// sessionStorage, with localStorage as the fallback (iOS Safari can drop the
// tab's copy, store/booking-persistence.js); a day at most, cleared once the
// booking is paid (clearBookingState).
export const ATTEMPT_KEY = 'gwmc_checkout_attempt'
const MAX_AGE_MS = 24 * 60 * 60 * 1000

const stores = () => {
  const out = []
  try { if (globalThis.sessionStorage) out.push(globalThis.sessionStorage) } catch (e) { /* blocked */ }
  try { if (globalThis.localStorage) out.push(globalThis.localStorage) } catch (e) { /* blocked */ }
  return out
}

const newKey = () => {
  try { if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID() } catch (e) { /* older Safari */ }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

export const readAttempt = (now = Date.now()) => {
  for (const s of stores()) {
    try {
      const a = JSON.parse(s.getItem(ATTEMPT_KEY) || 'null')
      if (a && a.key && now - (a.savedAt || 0) < MAX_AGE_MS) return a
    } catch (e) { /* unreadable: try the next */ }
  }
  return { key: newKey() }
}

export const saveAttempt = (changes, now = Date.now()) => {
  const next = { ...readAttempt(now), ...changes, savedAt: now }
  const raw = JSON.stringify(next)
  for (const s of stores()) { try { s.setItem(ATTEMPT_KEY, raw) } catch (e) { /* quota, private mode */ } }
  return next
}

export const clearAttempt = () => {
  for (const s of stores()) { try { s.removeItem(ATTEMPT_KEY) } catch (e) { /* ignore */ } }
}

// The guest this tab signed up can pay again while the same mobile and
// WhatsApp answer stand and its token is still the one in use.
export const sameGuest = (attempt, { mobile, optedIn, token }) =>
  !!(attempt?.guest && token && attempt.guest.token === token &&
    attempt.guest.mobile === mobile && attempt.guest.optedIn === !!optedIn)
